import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  AlertCircle,
  Database
} from 'lucide-react';
import { useScheduleExecutionHistory } from '@/hooks/useDataCollection';
import { Schedule } from '@/services/api/scheduling';
import { cn } from '@/lib/utils';
import {
  normalizeStatus,
  isCompletedStatus,
  isFailedStatus,
  isRunningStatus
} from '@/utils/executionStatus';
import { ExecutionHistory } from '@/services/api/dataCollection';

interface ScheduleExecutionHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: Schedule | null;
}

export const ScheduleExecutionHistoryDrawer: React.FC<ScheduleExecutionHistoryDrawerProps> = ({
  open,
  onOpenChange,
  schedule
}) => {
  // Use schedule_id filter to get executions from the backend
  const {
    data: executionHistory,
    isLoading,
    error,
    refetch
  } = useScheduleExecutionHistory(schedule?.id || null, 100, { enabled: open && !!schedule });

  // Filter to only show scheduled runs (trigger_mode === 'scheduled')
  // This ensures we only show runs that were triggered by the schedule, not manual runs
  const filteredExecutions = React.useMemo(() => {
    if (!executionHistory || !schedule) return [];
    return executionHistory.filter((exec: ExecutionHistory) => {
      // If the execution has a schedule_id that matches, it's from this schedule
      if (exec.schedule_id === schedule.id) return true;
      // Otherwise, filter by task_name AND trigger_mode as fallback
      return exec.task_name === schedule.task_name && exec.trigger_mode === 'scheduled';
    });
  }, [executionHistory, schedule]);

  // Get status badge
  const getStatusBadge = (status: string) => {
    const normalizedStatus = normalizeStatus(status);

    if (isCompletedStatus(normalizedStatus)) {
      return (
        <Badge
          variant='outline'
          className='bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400'
        >
          <CheckCircle className='h-3 w-3 mr-1' />
          Completed
        </Badge>
      );
    }
    if (isFailedStatus(normalizedStatus)) {
      return (
        <Badge
          variant='outline'
          className='bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
        >
          <XCircle className='h-3 w-3 mr-1' />
          Failed
        </Badge>
      );
    }
    if (isRunningStatus(normalizedStatus)) {
      return (
        <Badge
          variant='outline'
          className='bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400'
        >
          <Loader2 className='h-3 w-3 mr-1 animate-spin' />
          Running
        </Badge>
      );
    }
    return <Badge variant='outline'>{status}</Badge>;
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate duration from timestamps if duration_seconds not available
  const calculateDuration = (exec: any): number | null => {
    if (exec.duration_seconds !== null && exec.duration_seconds !== undefined) {
      return exec.duration_seconds;
    }
    // Calculate from started_at and finished_at
    if (exec.started_at && (exec.finished_at || exec.completed_at)) {
      const start = new Date(exec.started_at).getTime();
      const end = new Date(exec.finished_at || exec.completed_at).getTime();
      return Math.round((end - start) / 1000);
    }
    return null;
  };

  // Format duration
  const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-[700px] sm:max-w-[700px]'>
        <SheetHeader>
          <SheetTitle className='flex items-center gap-2'>
            <Database className='h-5 w-5' />
            Schedule Execution History
          </SheetTitle>
          <SheetDescription>
            {schedule ? (
              <>
                Scheduled runs for: <span className='font-medium'>{schedule.schedule_name}</span>
                <br />
                <span className='text-xs'>
                  Only showing runs triggered by the schedule (not manual triggers)
                </span>
              </>
            ) : (
              'Select a schedule to view history'
            )}
          </SheetDescription>
        </SheetHeader>

        <div className='mt-6'>
          {/* Header with refresh button */}
          <div className='flex items-center justify-between mb-4'>
            <div className='text-sm text-muted-foreground'>
              {filteredExecutions.length} execution{filteredExecutions.length !== 1 ? 's' : ''}{' '}
              found
            </div>
            <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className='flex items-center justify-center h-64'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className='flex flex-col items-center justify-center h-64 gap-4'>
              <AlertCircle className='h-8 w-8 text-destructive' />
              <p className='text-muted-foreground text-center'>
                Failed to load execution history
                <br />
                <span className='text-sm'>
                  {error instanceof Error ? error.message : 'Unknown error'}
                </span>
              </p>
              <Button variant='outline' onClick={() => refetch()}>
                <RefreshCw className='h-4 w-4 mr-2' />
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && filteredExecutions.length === 0 && (
            <div className='flex flex-col items-center justify-center h-64 gap-4'>
              <Clock className='h-12 w-12 text-muted-foreground' />
              <div className='text-center'>
                <h4 className='font-medium'>No Scheduled Runs Found</h4>
                <p className='text-sm text-muted-foreground'>
                  No scheduled executions found for this schedule.
                </p>
                <p className='text-xs text-muted-foreground mt-1'>
                  Manual triggers are not shown here.
                </p>
              </div>
            </div>
          )}

          {/* Executions table */}
          {!isLoading && !error && filteredExecutions.length > 0 && (
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Run ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExecutions.map((execution, index) => (
                    <TableRow key={`${execution.run_id || execution.id || 'exec'}-${index}`}>
                      <TableCell className='font-medium'>
                        {formatDate(execution.started_at)}
                      </TableCell>
                      <TableCell>{getStatusBadge(execution.status)}</TableCell>
                      <TableCell className='text-muted-foreground'>
                        {formatDuration(calculateDuration(execution))}
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {(execution.records ?? execution.records_processed)?.toLocaleString() ||
                          '-'}
                      </TableCell>
                      <TableCell>
                        <span className='font-mono text-xs text-muted-foreground'>
                          {execution.run_id?.slice(0, 12) || '-'}...
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Schedule info */}
          {!isLoading && !error && schedule && (
            <div className='mt-6 p-4 bg-muted/30 rounded-lg'>
              <h4 className='font-medium text-sm mb-2'>Schedule Details</h4>
              <div className='grid grid-cols-2 gap-2 text-sm'>
                <div>
                  <span className='text-muted-foreground'>Cron:</span>{' '}
                  <span className='font-mono text-xs'>{schedule.cron_expression}</span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Timezone:</span>{' '}
                  <span className='text-xs'>{schedule.time_zone}</span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Total Runs:</span>{' '}
                  <span className='font-medium'>{schedule.run_count}</span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Status:</span>{' '}
                  <Badge
                    variant={schedule.is_enabled ? 'default' : 'secondary'}
                    className='text-xs'
                  >
                    {schedule.is_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ScheduleExecutionHistoryDrawer;
